import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Col,
  Grid,
  Input,
  InputContainer,
  Title,
} from '@common/components';
import { useAuth } from '@auth/hooks';
import { useAppDispatch } from '@app/hooks';
import { useInputErrors } from '@app/hooks/useInputErrors';
import { isEmail, isEmpty } from '@app/util/validations';
import { useCreateSupportMutation } from '@app/api/support';
import { setErrorModal } from '@app/slices/modals';
import { AlertModal } from '@app/components';

import profileClasses from './SupportView.module.scss';

export default function SupportView() {
  const { user } = useAuth();
  const { t } = useTranslation('app\\SupportView');

  const [email, setEmail] = useState(user ? user.email : '');
  const [fullName, setFullName] = useState(
    user ? `${user.first_name} ${user.last_name}` : '',
  );
  const [remarks, setRemarks] = useState('');
  const [createSupport, { isLoading }] = useCreateSupportMutation();

  const { errors, resetErrors, setErrors, updateErrors } = useInputErrors();
  const dispatch = useAppDispatch();

  const onValidate = () => {
    let isValid = resetErrors();
    setErrors({});

    if (isEmpty(email)) {
      isValid = updateErrors({
        email: t('inputEmailValidationRequired'),
      });
    } else if (!isEmail(email)) {
      isValid = updateErrors({
        email: t('inputEmailValidationFormat'),
      });
    }
    if (isEmpty(fullName)) {
      isValid = updateErrors({
        fullName: t('inputFullNameValidationRequired'),
      });
    }
    if (isEmpty(remarks)) {
      isValid = updateErrors({
        remarks: t('inputRemarksValidationFormat'),
      });
    }
    return isValid;
  };
  const [showModal, setShowModal] = useState(false);

  const handleSubmit = async () => {
    if (onValidate() && email && fullName) {
      try {
        const obj = {
          email,
          first_name: fullName,
          remarks,
        };

        const data = await createSupport(obj).unwrap();

        if (data.success) {
          setShowModal(true);
        }
      } catch (error) {
        dispatch(
          setErrorModal({
            ...error,
            ...{ data: { message: t('Unauthorized') } },
          }),
        );
      }
    }
    return null;
  };
  return (
    <div className={profileClasses.userProfile}>
      <Title color="primary" className={profileClasses.title}>
        {t('lblTitle')}
      </Title>
      <form onSubmit={() => {}}>
        <Grid align="middle" justify="center">
          <Col xs={12} sm={6}>
            <InputContainer
              className={profileClasses.input}
              placeholder={t('inputNamePlaceholder')}
              name="firstName"
              error={errors.fullName}
            >
              <Input
                name="firstName"
                fullWidth
                type="text"
                placeholder={t('inputNamePlaceholder')}
                onChange={setFullName}
                value={fullName}
              />
            </InputContainer>
          </Col>
          <Col xs={12} sm={6}>
            <InputContainer
              className={profileClasses.input}
              placeholder={t('inputEmailPlaceholder')}
              name="email"
              error={errors.email}
            >
              <Input
                name="email"
                fullWidth
                type="email"
                placeholder={t('inputEmailPlaceholder')}
                onChange={setEmail}
                value={email}
              />
            </InputContainer>
          </Col>
          <Col md={12}>
            <InputContainer
              className={profileClasses.input}
              placeholder={t('inputCommentsPlaceholder')}
              name="remarks"
              error={errors.remarks}
            >
              <textarea
                className={profileClasses.textarea}
                rows={4}
                onChange={(e) => setRemarks(e.target.value)}
              />
            </InputContainer>
          </Col>
        </Grid>
        <Grid
          className={profileClasses.buttonContainer}
          align="top"
          gutter={20}
          justify="center"
        >
          <Col xss={12}>
            <Button
              className={profileClasses.button}
              variant="flat"
              color="accent"
              onClick={() => handleSubmit()}
              disabled={isLoading}
            >
              {t('lblSend')}
            </Button>
          </Col>
        </Grid>
        <AlertModal
          show={showModal}
          type="done"
          title={t('modalTitle')}
          text={t('modalText')}
        />
      </form>
    </div>
  );
}
